/**
 * lazyPage — route-level code splitting that keeps SSR output intact.
 *
 * React.lazy alone renders the Suspense fallback in renderToString, so a lazily
 * loaded page would reach crawlers (and ad visitors, before hydration) as a
 * loading spinner. A page created here instead:
 *
 *  - on the server: registers its pending import; entry-server re-renders once
 *    every import started by the previous pass has settled, so the HTML always
 *    contains the real page body. The chunk ids of the pages actually rendered
 *    are reported so the server can emit <link rel="modulepreload"> for them
 *    (opt out with { modulePreload: false } for chunks that are not urgent).
 *  - on the client: suspends during hydration, which makes React keep the server
 *    HTML on screen and hydrate that boundary as soon as the chunk arrives. Once
 *    loaded, it renders the resolved component directly (no extra suspension on
 *    later navigations back to the same page).
 */
import { lazy, type ComponentType, type FunctionComponent } from "react";

type PageModule<P> = { default: ComponentType<P> };

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type LazyPage<P extends object = {}> = FunctionComponent<P> & {
  /** Vite manifest key of the module this page lives in, e.g. "src/pages/Contact.tsx". */
  chunkId: string;
  preload: () => Promise<void>;
};

const inflight = new Set<Promise<unknown>>();
let renderedChunkIds: Set<string> | null = null;

const CHUNK_RELOAD_KEY = "lkw_chunk_reload";

/**
 * A deploy replaces hashed chunks; a tab opened before it would 404 on its next
 * lazy import. Reload once to pick up the new build instead of showing an error.
 */
function reloadOnceForStaleChunk(error: unknown): never {
  if (typeof window !== "undefined") {
    try {
      if (!window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    } catch {
      /* storage unavailable — fall through to the error boundary */
    }
  }
  throw error;
}

function clearStaleChunkGuard() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function lazyPage<P extends object = {}>(
  chunkId: string,
  load: () => Promise<PageModule<P>>,
  options: { modulePreload?: boolean } = {},
): LazyPage<P> {
  const reportForPreload = options.modulePreload ?? true;
  let Resolved: ComponentType<P> | null = null;
  let pending: Promise<void> | null = null;

  const preload = () => {
    if (!pending) {
      const attempt: Promise<void> = load()
        .then(mod => {
          Resolved = mod.default;
          clearStaleChunkGuard();
        })
        .catch(error => {
          pending = null;
          return reloadOnceForStaleChunk(error);
        })
        .finally(() => {
          inflight.delete(attempt);
        });
      pending = attempt;
      inflight.add(attempt);
    }
    return pending;
  };

  const Lazy = lazy(() => preload().then(() => ({ default: Resolved as ComponentType<P> })));

  function Page(props: P) {
    if (reportForPreload) renderedChunkIds?.add(chunkId);
    const Component = Resolved;
    return Component ? <Component {...props} /> : <Lazy {...props} />;
  }

  const page = Page as LazyPage<P>;
  page.chunkId = chunkId;
  page.preload = preload;
  page.displayName = `LazyPage(${chunkId})`;
  return page;
}

/**
 * Server only. Renders until no lazy page is still loading, so the returned HTML
 * never contains a Suspense fallback for a route component.
 */
export async function renderWithLazyPages(
  render: () => string,
  maxPasses = 4,
): Promise<{ html: string; chunkIds: string[] }> {
  let html = "";
  let chunkIds: string[] = [];
  for (let pass = 0; pass < maxPasses; pass += 1) {
    renderedChunkIds = new Set();
    try {
      html = render();
      chunkIds = Array.from(renderedChunkIds);
    } finally {
      renderedChunkIds = null;
    }
    if (inflight.size === 0) break;
    await Promise.allSettled(Array.from(inflight));
  }
  return { html, chunkIds };
}
